import type { Resource } from "fhir/r4";
import fhirpath from "fhirpath";

/** Evaluates a FHIRPath expression against a FHIR resource, returns first result as string if possible, or `undefined. */
export const evaluateFhirPathString = (resource: Resource, expression: string): string | undefined => {
	const [first] = evaluateFhirPath(resource, expression);
	if (typeof first === "string") return first;
	if (typeof first === "number" && Number.isFinite(first)) return String(first);
	return undefined;
};

/**
 * Evaluates `expression` as a FHIRPath boolean predicate. Returns `true` only when the expression yields exactly
 * `[true]`. Any other outcome — `[false]`, an empty result, a non-boolean first value, a Promise, or an evaluation
 * error — yields `false`.
 *
 * Both call sites (SDC `enableWhenExpression` and `questionnaire-constraint`) treat an unevaluable predicate as "did
 * not pass" — disabled / violated — so we centralise that interpretation here rather than coercing arbitrary truthy
 * values.
 *
 * @see https://hl7.org/fhir/uv/sdc/StructureDefinition-sdc-questionnaire-enableWhenExpression.html
 * @see https://hl7.org/fhir/extensions/StructureDefinition-questionnaire-constraint.html
 */
export const evaluateFhirPathBoolean = (resource: Resource, expression: string): boolean => {
	const result = evaluateFhirPath(resource, expression);
	return result.length === 1 && result[0] === true;
};

/**
 * Thin, synchronous wrapper around `fhirpath.evaluate`.
 *
 * `fhirpath.evaluate` can return a `Promise` when invoked with async context resolvers (e.g. terminology lookups). We
 * intentionally do not support that here — assessments scoring and validation must stay synchronous — so any Promise
 * result is treated as "no result". Evaluation errors are swallowed and return an empty array so callers can keep their
 * happy-path code uncluttered.
 *
 * The resource is auto-bound as the standard `%resource` and `%context` environment variables so SDC expressions like
 * `enableWhenExpression` and `questionnaire-constraint` "just work".
 */
const evaluateFhirPath = (resource: Resource, expression: string): unknown[] => {
	try {
		const result = fhirpath.evaluate(resource, expression, { resource, context: resource });
		if (result instanceof Promise) {
			throw new Error("Async FHIRPath evaluation is not supported.");
		}
		return result;
	} catch {
		return [];
	}
};
